import { ListFilter } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import SearchField from "../SearchField.tsx";
import type { FilterSpec } from "./specs.ts";

const SEARCH_THRESHOLD = 8;

interface AddFilterMenuProps {
  specs: FilterSpec[];
  onPick: (key: string) => void;
}

/** "+ Filter": the filters not shown as pills yet, grouped, searchable when many. */
export default function AddFilterMenu({ specs, onPick }: AddFilterMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  if (specs.length === 0) return null;

  const q = query.trim().toLowerCase();
  const shown = q ? specs.filter((s) => s.label.toLowerCase().includes(q)) : specs;
  const groups: [string, FilterSpec[]][] = [];
  for (const s of shown) {
    const name = s.group ?? "";
    const group = groups.find(([g]) => g === name);
    if (group) group[1].push(s);
    else groups.push([name, [s]]);
  }

  const close = () => {
    setAnchor(null);
    setQuery("");
  };

  return (
    <>
      <Button
        size="small"
        color="neutral"
        startIcon={<ListFilter />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        sx={{ borderRadius: 999, px: 1.5 }}
      >
        Filter
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.5, width: 260 } } }}
      >
        {specs.length > SEARCH_THRESHOLD && (
          <Box sx={{ px: 1, pt: 1 }}>
            <SearchField value={query} onChange={setQuery} placeholder="Filter by…" autoFocus fullWidth />
          </Box>
        )}
        {/* Flat children: a MenuList walks its direct children for arrow-key focus. */}
        <MenuList sx={{ maxHeight: 380, overflowY: "auto" }}>
          {groups.flatMap(([group, items]) => [
            group ? <ListSubheader key={`group-${group}`}>{group}</ListSubheader> : null,
            ...items.map((s) => (
              <MenuItem
                key={s.key}
                onClick={() => {
                  close();
                  onPick(s.key);
                }}
              >
                <ListItemText primary={s.label} />
              </MenuItem>
            )),
          ])}
          {shown.length === 0 && (
            <Typography variant="body2" sx={{ color: "text.secondary", px: 1.75, py: 1 }}>
              No filter matches
            </Typography>
          )}
        </MenuList>
      </Popover>
    </>
  );
}
